/**
 * Program-scoped document listing for the student portal.
 *
 * Uses TMS table `program_document_templates`:
 * - If mappings exist for the program → only those template_ids
 * - If no mappings → FALLBACK: show all generated docs (legacy)
 */

const DRIVE_VIEW = (id) => `https://drive.google.com/uc?export=view&id=${id}`;
const DRIVE_DOWNLOAD = (id) => `https://drive.google.com/uc?export=download&id=${id}`;
const DRIVE_PREVIEW = (id) => `https://drive.google.com/file/d/${id}/preview`;

export const DEFAULT_LEGACY_SECTIONS = ['Initial Registration', 'Registration', 'Default'];

/**
 * Resolve program_type + program_code for a registration row.
 * Prefers full certification, then standalone course type.
 */
export async function resolveProgramForRegistration(supabase, registration) {
  if (!registration) return null;

  const nestedCode =
    registration.full_certification_runs?.full_certification?.code ||
    registration.full_certification_runs?.full_cert_type_code ||
    null;

  if (nestedCode) {
    return { programType: 'full_certification', programCode: nestedCode };
  }

  if (registration.draft_cert_run_code) {
    const { data, error } = await supabase
      .from('full_certification_runs')
      .select('full_cert_type_code')
      .eq('id', registration.draft_cert_run_code)
      .maybeSingle();

    if (!error && data?.full_cert_type_code) {
      return {
        programType: 'full_certification',
        programCode: data.full_cert_type_code,
      };
    }
  }

  if (registration.draft_course_run_code) {
    const { data, error } = await supabase
      .from('course_run')
      .select('course_type_code')
      .eq('id', registration.draft_course_run_code)
      .maybeSingle();

    if (!error && data?.course_type_code) {
      return {
        programType: 'course_type',
        programCode: data.course_type_code,
      };
    }
  }

  return null;
}

/**
 * Load TMS template mappings for a program.
 * Returns [] when none configured (caller treats as legacy fallback).
 */
export async function fetchProgramTemplateMappings(supabase, programType, programCode) {
  if (!programType || !programCode) return [];

  const { data, error } = await supabase
    .from('program_document_templates')
    .select('template_id, display_order')
    .eq('program_type', programType)
    .eq('program_code', programCode)
    .order('display_order', { ascending: true });

  if (error) throw error;
  return data || [];
}

/**
 * Map a document_generator_docs row to portal UI shape.
 * Student-facing: PDF only. Google Docs / raw templates are never exposed.
 * Docs with no template_id are only included by callers in legacy mode.
 */
export function toPortalDocumentItem(doc, templateFallback = null) {
  const title =
    doc.document_name ||
    templateFallback?.name ||
    'Document';

  if (doc.pdf_file_id) {
    return {
      id: doc.id,
      templateId: doc.template_id,
      title,
      pdfFileId: doc.pdf_file_id,
      pdfUrl: DRIVE_VIEW(doc.pdf_file_id),
      pdfDownloadUrl: DRIVE_DOWNLOAD(doc.pdf_file_id),
      previewUrl: DRIVE_PREVIEW(doc.pdf_file_id),
      isPdf: true,
      status: 'done',
    };
  }

  // Row exists but PDF not ready yet (even if google_doc_id is set) → still generating
  return {
    id: doc.id,
    templateId: doc.template_id,
    title,
    isGenerating: true,
    isPdf: false,
    status: 'generating',
  };
}

function notStartedPlaceholder(templateId, title = 'Document') {
  return {
    id: `template-${templateId}`,
    templateId,
    title,
    isGenerating: true,
    isPdf: false,
    status: 'not_started',
  };
}

/**
 * Build the portal document list for one or more registrations belonging to the same program.
 *
 * Students only need `document_generator_docs` (their generated PDFs).
 * Raw `google_document_templates` are staff/owner-only and are not queried here.
 *
 * @returns {{
 *   documentsList: Array,
 *   allowedTemplateIds: string[] | null,
 *   expectedCount: number,
 *   hasCustomSet: boolean
 * }}
 * `allowedTemplateIds === null` means legacy (no custom set).
 */
export async function buildPortalDocumentsList(supabase, {
  registrationIds,
  programType = null,
  programCode = null,
  // Kept for call-site compatibility; legacy no longer reads raw templates by section.
  legacySections: _legacySections = DEFAULT_LEGACY_SECTIONS,
}) {
  if (!registrationIds || registrationIds.length === 0) {
    return {
      documentsList: [],
      allowedTemplateIds: null,
      expectedCount: 0,
      hasCustomSet: false,
    };
  }

  const mappings = await fetchProgramTemplateMappings(supabase, programType, programCode);
  const allowedTemplateIds =
    mappings.length > 0 ? mappings.map((m) => m.template_id) : null;
  const hasCustomSet = allowedTemplateIds !== null;

  // Own generated docs only — no join to google_document_templates (staff-only).
  const { data: allDocs, error: docsError } = await supabase
    .from('document_generator_docs')
    .select(`
      id,
      document_name,
      pdf_file_id,
      template_id,
      created_at
    `)
    .in('registration_id', registrationIds)
    .order('created_at', { ascending: true });

  if (docsError) throw docsError;

  // Latest generated row wins per template_id
  const docsByTemplate = new Map();
  (allDocs || []).forEach((doc) => {
    if (doc.template_id) {
      docsByTemplate.set(doc.template_id, doc);
    }
  });

  if (hasCustomSet) {
    // Custom set from program_document_templates IDs only (not raw template rows).
    // Missing rows stay as not_started placeholders so enrollment can trigger generation.
    const documentsList = allowedTemplateIds.map((templateId) => {
      const doc = docsByTemplate.get(templateId);
      if (doc) return toPortalDocumentItem(doc);
      return notStartedPlaceholder(templateId, 'Document');
    });

    return {
      documentsList,
      allowedTemplateIds,
      expectedCount: allowedTemplateIds.length,
      hasCustomSet: true,
    };
  }

  // FALLBACK (legacy): show generated docs only — no raw template catalog.
  const documentsList = (allDocs || []).map((doc) => toPortalDocumentItem(doc));

  // Empty list: one placeholder so enrollment still auto-triggers copy-google-doc.
  if (documentsList.length === 0) {
    documentsList.push(notStartedPlaceholder('pending-generation', 'Documents'));
    return {
      documentsList,
      allowedTemplateIds: null,
      expectedCount: 0,
      hasCustomSet: false,
    };
  }

  return {
    documentsList,
    allowedTemplateIds: null,
    expectedCount: documentsList.length,
    hasCustomSet: false,
  };
}

/**
 * Count completed PDFs among allowed templates.
 * Used by polling / generation-complete checks.
 */
export function countCompletedPortalDocs(generatedDocs, allowedTemplateIds) {
  const docs = generatedDocs || [];
  const isComplete = (d) => Boolean(d.pdf_file_id);

  if (allowedTemplateIds && allowedTemplateIds.length > 0) {
    return docs.filter(
      (d) => d.template_id && allowedTemplateIds.includes(d.template_id) && isComplete(d)
    ).length;
  }

  return docs.filter(isComplete).length;
}

/**
 * Open PDF preview for a portal document item. Google Docs are never opened.
 */
export function openPortalDocument(doc) {
  if (!doc?.pdfFileId) return;
  window.open(DRIVE_PREVIEW(doc.pdfFileId), '_blank', 'noopener,noreferrer');
}
